import { AlertTriangle, BadgeCheck, CalendarClock, Hourglass, ListTodo, PhoneCall, Plus, RefreshCcw, ShoppingBag, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Kpi, Section } from '@/components/common/Blocks'
import { PageHeader } from '@/components/common/PageHeader'
import { Paged, Pager } from '@/components/common/Pager'
import { EmptyState } from '@/components/common/States'
import { usePagination } from '@/components/common/usePagination'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useFollowups, useOrders, useRenewals } from './api'
import { CustomerPicker } from './components/CrmBits'
import { FollowupForm } from './components/forms/SalesForms'
import { OrderCard } from './components/OrderCard'
import { FollowupList, RenewalList } from './components/SalesLists'
import { useCrmDeletes } from './components/useCrmDeletes'
import { useScopedDialog } from './components/useScopedDialog'
import { CRM_WRITE_PERM } from './permissions'
import { RENEWAL_KINDS, type Followup, type OrderStatus, type Renewal, type RenewalKind } from './types'
import { useCrm } from './ui'

function FilterTabs<V extends string>({ value, onChange, items, label }: { value: V; onChange: (v: V) => void; items: { value: V; count: number }[]; label: (v: V) => string }) {
  return (
    <div className="max-w-full overflow-x-auto scrollbar-none">
      <Tabs value={value} onValueChange={(v) => onChange(v as V)}>
        <TabsList size="sm">
          {items.map((i) => (
            <TabsTrigger key={i.value} value={i.value}>
              {label(i.value)}
              <span className="ml-1 tabular-nums text-muted-foreground">{i.count}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  )
}

export function OrdersPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const orders = useOrders().data ?? []
  const deletes = useCrmDeletes()
  const scoped = useScopedDialog()
  const [status, setStatus] = useState<OrderStatus | 'all'>('pending')
  const count = (s: OrderStatus | 'all') => orders.filter((o) => s === 'all' || o.status === s).length
  const rows = orders.filter((o) => status === 'all' || o.status === status)

  const { pageRows, pager } = usePagination(rows, status)
  return (
    <>
      <PageHeader eyebrow={t('nav.groups.customers')} title={t('crm.ordersPage.title')} description={t('crm.ordersPage.subtitle')} />
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Kpi icon={Hourglass} tone="warning" label={t('crm.orderStatus.pending')} value={count('pending')} footer={t('crm.ordersPage.pendingHint')} />
          <Kpi icon={BadgeCheck} tone="success" label={t('crm.orderStatus.approved')} value={count('approved')} />
          <Kpi icon={XCircle} tone="danger" label={t('crm.orderStatus.rejected')} value={count('rejected')} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="mr-auto flex items-center gap-2 font-semibold">
            <ShoppingBag className="size-4 text-muted-foreground" aria-hidden />
            {t('crm.order.title')}
          </h2>
          <FilterTabs value={status} onChange={setStatus} label={(v) => (v === 'all' ? t('common.all') : t(`crm.orderStatus.${v}`))} items={(['pending', 'approved', 'rejected', 'all'] as const).map((v) => ({ value: v, count: count(v) }))} />
        </div>
        {rows.length === 0 ? (
          <div className="rounded-xl border bg-card shadow-card">
            <EmptyState icon={ShoppingBag} title={t('crm.ordersPage.empty')} />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {pageRows.map((o) => (
                <OrderCard
                  key={o.id}
                  order={o}
                  showCustomer
                  canWrite={can('sales:order:write')}
                  canApprove={can('sales:order:approve')}
                  onApprove={() => scoped.open(o.customerId, { kind: 'approveOrder', order: o })}
                  onReject={() => scoped.open(o.customerId, { kind: 'rejectOrder', order: o })}
                  onDelete={() => void deletes.order(o)}
                />
              ))}
            </div>
            <Pager {...pager} className="rounded-xl border bg-card shadow-card" />
          </>
        )}
      </div>
      {scoped.host}
    </>
  )
}

const RENEWAL_FILTERS = ['open', 'overdue', 'soon', 'renewed', 'all'] as const
type RenewalFilter = (typeof RENEWAL_FILTERS)[number]
const isOpen = (r: Renewal) => r.status === 'upcoming' || r.status === 'contacted'
const matchRenewal = (r: Renewal, f: RenewalFilter) =>
  f === 'all' || (f === 'open' ? isOpen(r) : f === 'overdue' ? isOpen(r) && r.daysLeft < 0 : f === 'soon' ? isOpen(r) && r.daysLeft >= 0 && r.daysLeft <= 30 : r.status === 'renewed')

export function RenewalsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const renewals = useRenewals().data ?? []
  const deletes = useCrmDeletes()
  const scoped = useScopedDialog()
  const [filter, setFilter] = useState<RenewalFilter>('open')
  const [kind, setKind] = useState<RenewalKind | 'all'>('all')
  const [customerId, setCustomerId] = useState('')
  const canWrite = can('renewals.write')
  const scopedRows = renewals.filter((r) => kind === 'all' || r.kind === kind)
  const rows = scopedRows.filter((r) => matchRenewal(r, filter))
  const n = (f: RenewalFilter) => scopedRows.filter((r) => matchRenewal(r, f)).length

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.customers')}
        title={t('crm.renewalsPage.title')}
        description={t('crm.renewalsPage.subtitle')}
        actions={
          canWrite ? (
            <>
              <CustomerPicker value={customerId} onChange={setCustomerId} />
              <Button disabled={!customerId} onClick={() => scoped.open(customerId, { kind: 'renewal', renewal: null })}>
                <Plus />
                {t('crm.renewal.add')}
              </Button>
            </>
          ) : null
        }
      />
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Kpi icon={AlertTriangle} tone="danger" label={t('crm.renewalsPage.overdue')} value={n('overdue')} footer={t('crm.renewalsPage.overdueHint')} />
          <Kpi icon={CalendarClock} tone="warning" label={t('crm.renewalsPage.soon')} value={n('soon')} footer={t('crm.renewalsPage.soonHint')} />
          <Kpi icon={PhoneCall} tone="info" label={t('crm.renewalStatus.contacted')} value={scopedRows.filter((r) => r.status === 'contacted').length} />
        </div>
        <Section
          title={t('crm.renewal.title')}
          icon={RefreshCcw}
          bodyClassName="p-0"
          actions={
            <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
              <Select size="sm" className="w-36" value={kind} onChange={(v) => setKind(v as typeof kind)} aria-label={t('crm.renewal.kind')} options={[{ value: 'all', label: t('common.all') }, ...RENEWAL_KINDS.map((k) => ({ value: k, label: t(`crm.renewalKind.${k}`) }))]} />
              <FilterTabs value={filter} onChange={setFilter} label={(v) => t(`crm.renewalsPage.filter.${v}`)} items={RENEWAL_FILTERS.map((v) => ({ value: v, count: n(v) }))} />
            </div>
          }
        >
          <Paged rows={rows} resetKey={[filter, kind, customerId]}>
            {(page) => <RenewalList rows={page} showCustomer canWrite={canWrite} onEdit={(r) => scoped.open(r.customerId, { kind: 'renewal', renewal: r })} onDelete={(r) => void deletes.renewal(r)} />}
          </Paged>
        </Section>
      </div>
      {scoped.host}
    </>
  )
}

const FOLLOWUP_FILTERS = ['open', 'overdue', 'today', 'done', 'all'] as const
type FollowupFilter = (typeof FOLLOWUP_FILTERS)[number]
const todayIso = () => new Date().toISOString().slice(0, 10)
const matchFollowup = (f: Followup, k: FollowupFilter, today: string) =>
  k === 'all' || (k === 'open' ? !f.done : k === 'overdue' ? f.overdue : k === 'today' ? !f.done && f.dueOn === today : f.done)

export function FollowupsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { staff } = useCrm()
  const followups = useFollowups().data ?? []
  const deletes = useCrmDeletes()
  const [filter, setFilter] = useState<FollowupFilter>('open')
  const [owner, setOwner] = useState('all')
  const [form, setForm] = useState<{ open: boolean; followup: Followup | null }>({ open: false, followup: null })
  const canWrite = can(CRM_WRITE_PERM)
  const today = todayIso()
  const mine = followups.filter((f) => owner === 'all' || f.ownerId === owner)
  const rows = mine.filter((f) => matchFollowup(f, filter, today))
  const n = (k: FollowupFilter) => mine.filter((f) => matchFollowup(f, k, today)).length

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.customers')}
        title={t('crm.followupsPage.title')}
        description={t('crm.followupsPage.subtitle')}
        actions={
          canWrite ? (
            <Button onClick={() => setForm({ open: true, followup: null })}>
              <Plus />
              {t('crm.followup.add')}
            </Button>
          ) : null
        }
      />
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Kpi icon={AlertTriangle} tone="danger" label={t('crm.followupsPage.filter.overdue')} value={n('overdue')} />
          <Kpi icon={CalendarClock} tone="warning" label={t('crm.followupsPage.filter.today')} value={n('today')} />
          <Kpi icon={ListTodo} tone="info" label={t('crm.followupsPage.filter.open')} value={n('open')} />
        </div>
        <Section
          title={t('crm.followup.title')}
          icon={ListTodo}
          bodyClassName="p-0"
          actions={
            <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
              <Select size="sm" className="w-44" value={owner} onChange={setOwner} aria-label={t('crm.followup.owner')} options={[{ value: 'all', label: t('crm.followupsPage.allOwners') }, ...staff.map((s) => ({ value: s.id, label: s.name }))]} />
              <FilterTabs value={filter} onChange={setFilter} label={(v) => t(`crm.followupsPage.filter.${v}`)} items={FOLLOWUP_FILTERS.map((v) => ({ value: v, count: n(v) }))} />
            </div>
          }
        >
          <Paged rows={rows} resetKey={[filter, owner]}>
            {(page) => <FollowupList rows={page} showCustomer canWrite={canWrite} onEdit={(f) => setForm({ open: true, followup: f })} onDelete={(f) => void deletes.followup(f)} />}
          </Paged>
        </Section>
      </div>
      {form.open ? <FollowupForm key={form.followup?.id ?? 'new'} followup={form.followup} customerId={form.followup?.customerId} onDone={() => setForm({ open: false, followup: null })} /> : null}
    </>
  )
}
